/**
 * CompositeNode - base class for behavior tree nodes with children (Selector, Sequence).
 */

import { BehaviorNode, type BehaviorNodeOptions } from '../BehaviorNode.js';

import type { Blackboard } from '../blackboard/Blackboard.js';

export interface CompositeNodeOptions extends BehaviorNodeOptions {
  children?: BehaviorNode[];
}

export abstract class CompositeNode extends BehaviorNode {
  protected children: BehaviorNode[] = [];
  protected currentChildIndex: number = 0;

  constructor(options: CompositeNodeOptions = {}) {
    super(options);
    this.children = options.children ?? [];
  }

  public addChild(child: BehaviorNode): void {
    this.children.push(child);
  }

  public addChildren(children: BehaviorNode[]): void {
    this.children.push(...children);
  }

  public removeChild(child: BehaviorNode): void {
    const index = this.children.indexOf(child);
    if (index !== -1) {
      this.children.splice(index, 1);
    }
  }

  public getChildren(): BehaviorNode[] {
    return [...this.children];
  }

  public getChildCount(): number {
    return this.children.length;
  }

  protected override onInitialize(blackboard: Blackboard): void {
    super.onInitialize(blackboard);
    // Initialize all children
    for (const child of this.children) {
      child.initialize(blackboard);
    }
  }

  protected override onReset(): void {
    super.onReset();
    this.currentChildIndex = 0;
    // Reset all children
    for (const child of this.children) {
      child.reset();
    }
  }

  protected override onDestroy(): void {
    super.onDestroy();
    // Destroy all children
    for (const child of this.children) {
      child.destroy();
    }
  }

  public override getDebugInfo(): { name: string; description: string; status: string; isExecuting: boolean; children: any[]; currentChildIndex: number } {
    const baseInfo = super.getDebugInfo();
    return {
      ...baseInfo,
      children: this.children.map(child => child.getDebugInfo()),
      currentChildIndex: this.currentChildIndex
    };
  }

  public override getCurrentlyExecutingChild(): BehaviorNode | null {
    if (this.isCurrentlyExecuting && this.currentChildIndex < this.children.length) {
      return this.children[this.currentChildIndex];
    }
    return null;
  }
}